import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  ArrowDropUp,
  ArrowDropDown,
  UnfoldMore,
} from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { applyRenderFunctions } from "../OrderComponents/columnRendererRegistry";
import { getPreference, setPreference } from "../../Utils/userPreferenceManger";
import { clearConfigIfOutdated, updateColumnConfigIfNeeded } from "../../Utils/columnVersionManager";
import { NUMERIC_COLUMNS, HIGH_PRECISION_NUMERIC } from "../../Constants/globalColConfig";
import OrderEntry from "../OrderEntry";
import { tokens } from "../../Common/designTokens";
import { isStagingOrder } from "../../Constants/stagingBlotter";

// Editable field configurations - centralized for easy maintenance
const EDITABLE_FIELDS = [
  { columnKey: "tradeQuantity", rendererPropName: "tradeQtyCellRenderer" },
  { columnKey: "endingQuantity", rendererPropName: "endingQtyCellRenderer" },
  { columnKey: "tradeValue", rendererPropName: "tradeValueCellRenderer" },
  { columnKey: "endingSecurityMarketValue", rendererPropName: "endingSecurityMarketValueCellRenderer" },
  { columnKey: "endingSecurityWeightPercentage", rendererPropName: "endingWeightCellRenderer" },
];

// helper to adjust alpha of rgba tokens
const withAlpha = (rgba, alpha) =>
  rgba.replace(/rgba\((\s*\d+\s*,\s*\d+\s*,\s*\d+\s*),\s*[\d.]+\s*\)/, `rgba($1, ${alpha})`);

// value color helper similar to main component
const getValueColor = (columnKey, isBuy, isSell) => {
  if (isSell) return tokens.colors.sellFontColor;
  if (isBuy) return tokens.colors.buyFontColor;
  if (["totalGL", "realizedGL", "unrealizedGL"].includes(columnKey)) {
    return tokens.colors.buyFontColor;
  }
  return tokens.colors.fontColour;
};

const BondTable = ({
  source = null,
  selectedColumns,
  filteredData,
  handleRowClick,
  handleViewJson,
  handleBuyClick,
  handleCusipClick,
  onOrderAdded,
  orderData,
  setOrderData,
  sectionKey = "default",
  onSort,
  sortConfig: externalSortConfig,
  selectedRows = [],
  onRowSelectionChange,
  onSelectAll,
  onClearSelection,
  onTradeQtyChange,
  tradeQtyCellRenderer,
  endingQtyCellRenderer,
  tradeValueCellRenderer,
  endingSecurityMarketValueCellRenderer,
  endingWeightCellRenderer,
  showCheckboxes = false, // <-- Add this prop
  onEditVirtualZeroPosition, // NEW
  onDeleteVirtualZeroPosition, // NEW
  allowEditWithoutSide = false,
}) => {
  const tableContainerRef = useRef(null);
  // Store the raw column data separate from the rendered columns
  const [rawColumns, setRawColumns] = useState([]);

  // Helper function to check if a column is editable (has a custom renderer)
  const isEditableColumn = (columnKey) => {
    const config = EDITABLE_FIELDS.find(field => field.columnKey === columnKey);
    if (!config) return false;

    // Map renderer prop names to actual renderer functions
    const rendererMap = {
      tradeQtyCellRenderer,
      endingQtyCellRenderer,
      tradeValueCellRenderer,
      endingSecurityMarketValueCellRenderer,
      endingWeightCellRenderer,
    };

    const renderer = rendererMap[config.rendererPropName];
    return typeof renderer === "function";
  };

  // Use useMemo to derive columns with render functions
  const columns = useMemo(() => {
    console.log(`BondTable: Applying render functions to columns: source=${source}, sectionKey=${sectionKey}`);
    // Clean up sectionKey if it's in URL params format (e.g., extract "1-2" from "tab=1-2")
    const cleanedSectionKey = sectionKey && sectionKey.includes('=')
      ? sectionKey.split('=').pop()
      : sectionKey;

    if (cleanedSectionKey !== sectionKey) {
      console.log(`Using cleaned section key: ${cleanedSectionKey} (original: ${sectionKey})`);
    }

    return applyRenderFunctions(rawColumns, source, cleanedSectionKey || sectionKey);
  }, [rawColumns, source, sectionKey]);

  // Replace internal sortConfig state with external one
  const sortConfig = externalSortConfig || { key: null, direction: "asc" };
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [orderEntryOpen, setOrderEntryOpen] = useState(false);
  const userRole = localStorage.getItem("role");

  // Load saved column widths on initial render
  useEffect(() => {
    try {
      const savedWidths = getPreference(`columnWidths_${sectionKey}`);
      if (savedWidths) {
        const parsedWidths = savedWidths;

        // Enforce minimum width for viewInventory column
        if ("viewInventory" in parsedWidths) {
          parsedWidths.viewInventory = Math.max(
            parsedWidths.viewInventory,
            140
          );
        }

        // Always force protocol column to 100px, ignore saved value
        parsedWidths.protocol = 100;

        setColumnWidths(parsedWidths);
      }
    } catch (e) {
      console.error("Error loading saved column widths:", e);
    }
  }, [sectionKey]);

  // Add new effect to save column widths whenever they change
  useEffect(() => {
    // Only save if we have widths to save and after initial load
    if (Object.keys(columnWidths).length > 0) {
      try {
        setPreference(`columnWidths_${sectionKey}`, columnWidths);
      } catch (e) {
        console.error("Error saving column widths:", e);
      }
    }
  }, [columnWidths, sectionKey]);

  // Resizing refs
  const resizingColumnRef = useRef(null);
  const initialWidthRef = useRef(0);
  const initialXRef = useRef(0);

  const calculateMaxContentWidth = (columnKey) => {
    // Force protocol column to be exactly 100px
    if (columnKey === "protocol") {
      return 100;
    }

    // Set default width for viewInventory column to 140px to prevent button cutting
    if (columnKey === "viewInventory") {
      return 140;
    }

    // Check if we have a saved width for this column
    const savedWidth = columnWidths[columnKey];
    if (savedWidth) {
      return savedWidth;
    }

    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    document.body.appendChild(span);

    // Get header width first
    span.textContent =
      selectedColumns.find((col) => col.key === columnKey)?.name || "";
    let maxWidth = span.offsetWidth;

    filteredData.forEach((row) => {
      const value = formatNumber(row[columnKey], columnKey, row);
      span.textContent = value?.toString() || "";
      maxWidth = Math.max(maxWidth, span.offsetWidth);
    });

    document.body.removeChild(span);

    // Use smaller padding for numeric columns vs text columns
    const isNumericColumn = [
      "qty",
      "quantity",
      "price",
      "minQty",
      "maxQty",
      "ytm",
      "ytw1",
    ].includes(columnKey);
    // const padding = isNumericColumn ? 27 : 32;
    const padding = 40;

    // Add appropriate padding to the content width
    const paddedWidth = maxWidth + padding;

    // For inventory source, use 85px as the maximum width
    if (source === "inventory") {
      return Math.min(paddedWidth, 85); // Cap at 85px for inventory source
    }

    // For non-inventory sources, keep the original 150px max width
    return Math.min(paddedWidth, 150); // 150 is imp for rfq branch view alignment of vertical line and start of table
  };

  useEffect(() => {
    if (sectionKey) {
      const savedOrder = getPreference(`columnOrder_${sectionKey}`);
      if (savedOrder) {
        try {
          const parsedOrder = savedOrder;
          // Only use saved order if it matches the selected columns (same length)
          if (parsedOrder.length === selectedColumns.length) {
            // Make sure parsedOrder has all the same columns as selectedColumns
            const selectedKeysSet = new Set(selectedColumns.map(col => col.key));
            const parsedKeysSet = new Set(parsedOrder.map(col => col.key));

            // Check if parsedOrder contains all keys from selectedColumns
            const hasSameColumns = [...selectedKeysSet].every(key => parsedKeysSet.has(key));

            if (hasSameColumns) {
              // Use the saved order since it's valid
              setRawColumns(parsedOrder);
              return;
            }
          }
        } catch (e) {
          console.error("Error parsing saved column order:", e);
        }
      }
    }
    // If no valid saved order, fall back to selectedColumns
    setRawColumns(selectedColumns);
    setColumnWidths((prev) => {
      const newWidths = { ...prev };
      selectedColumns.forEach((col) => {
        if (col.key === "protocol") {
          newWidths[col.key] = 100;
        } else if (!(col.key in newWidths)) {
          newWidths[col.key] = calculateMaxContentWidth(col.key);
        }
      });
      Object.keys(newWidths).forEach((key) => {
        if (!selectedColumns.some((col) => col.key === key)) {
          delete newWidths[key];
        }
      });
      newWidths.protocol = 100;

      return newWidths;
    });
  }, [selectedColumns, filteredData]);

  // Add this useEffect to reset scroll
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [filteredData]);

  // **Sorting Logic**
  // Modify handleSort to use external sort handler
  const handleSort = (columnKey) => {
    const newDirection =
      sortConfig?.key === columnKey && sortConfig?.direction === "asc"
        ? "desc"
        : "asc";
    onSort(columnKey, newDirection);
  };

  // **Drag-and-Drop Logic**
  const handleDragEnd = (result) => {
    if (!result.destination || (showCheckboxes && result.source.index === 0) || (showCheckboxes && result.destination.index === 0)) {
      return;
    }

    const reorderedColumns = [...rawColumns];
    const [movedColumn] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, movedColumn);

    // Update local state
    setRawColumns(reorderedColumns);

    // Save using role-based preferences
    if (sectionKey) {
      // Save current visible column order
      setPreference(`columnOrder_${sectionKey}`, reorderedColumns);

      // Update full column order while maintaining invisible columns
      const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
      if (savedFullOrder) {
        try {
          const fullOrderedColumns = savedFullOrder;

          // Create a map of visible column positions
          const newPositions = {};
          reorderedColumns.forEach((col, idx) => {
            newPositions[col.key] = idx;
          });

          // Create sets for quick lookup
          const visibleKeys = new Set(reorderedColumns.map(col => col.key));
          const fullOrderKeys = new Set(fullOrderedColumns.map(col => col.key));

          // Reorder visible columns and keep invisible ones in their relative positions
          const newFullOrder = [];

          // First add all visible columns in their new order
          reorderedColumns.forEach(col => {
            newFullOrder.push(col);
          });

          // Then add all invisible columns in their original order
          fullOrderedColumns.forEach(col => {
            if (!visibleKeys.has(col.key)) {
              newFullOrder.push(col);
            }
          });

          // Save the updated full order
          setPreference(`fullColumnOrder_${sectionKey}`, newFullOrder);

          // Also update selected columns to match current order
          const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
          if (savedSelectedColumns) {
            const selectedColumns = savedSelectedColumns;
            const reorderedSelected = reorderedColumns.filter(col =>
              selectedColumns.some(selected => selected.key === col.key)
            );
            setPreference(`selectedColumns_${sectionKey}`, reorderedSelected);
          }
        } catch (e) {
          console.error("Error updating full column order:", e);
        }
      }
    }
  };

  // Update useEffect for column initialization - now only sets raw columns
  useEffect(() => {
    console.log(`BondTable: Initializing columns with source=${source}, sectionKey=${sectionKey}`);

    // First check and initialize selectedColumns if not present
    const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);
    if (!savedSelectedColumns && selectedColumns) {
      setPreference(`selectedColumns_${sectionKey}`, selectedColumns);
      console.log(`Initialized selectedColumns_${sectionKey}`);
    }

    // Try to get saved column order using role-based preferences first
    if (sectionKey) {
      const savedOrder = getPreference(`columnOrder_${sectionKey}`);
      if (savedOrder) {
        try {
          const parsedOrder = savedOrder;
          // Only use saved order if it matches current selection length
          if (parsedOrder.length === selectedColumns.length) {
            setRawColumns(parsedOrder);
            return;
          }
        } catch (e) {
          console.error("Error parsing saved column order:", e);
        }
      }
    }

    // Fall back to selectedColumns if no valid saved order
    setRawColumns(selectedColumns);

    // Initialize fullColumnOrder if not already set
    if (sectionKey && selectedColumns) {
      const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
      if (!savedFullOrder) {
        setPreference(`fullColumnOrder_${sectionKey}`, selectedColumns);
        // Also initialize columnOrder
        setPreference(`columnOrder_${sectionKey}`, selectedColumns);
      }
    }
  }, [selectedColumns, sectionKey, source]);

  // Update when selected columns change
  useEffect(() => {
    if (selectedColumns && selectedColumns.length > 0) {
      console.log(`BondTable: Updating columns after selectedColumns change`);
      if (sectionKey) {
        const savedOrder = getPreference(`columnOrder_${sectionKey}`);
        if (savedOrder) {
          try {
            const parsedOrder = savedOrder;
            // Only use saved order if it matches the selected columns (same length)
            if (parsedOrder.length === selectedColumns.length) {
              // Make sure parsedOrder has all the same columns as selectedColumns
              const selectedKeysSet = new Set(selectedColumns.map(col => col.key));
              const parsedKeysSet = new Set(parsedOrder.map(col => col.key));

              // Check if parsedOrder contains all keys from selectedColumns
              const hasSameColumns = [...selectedKeysSet].every(key => parsedKeysSet.has(key));

              if (hasSameColumns) {
                // Use the saved order since it's valid
                setRawColumns(parsedOrder);
                return;
              }
            }
          } catch (e) {
            console.error("Error parsing saved column order:", e);
          }
        }
      }
      setRawColumns(selectedColumns);
    }
  }, [selectedColumns]);

  // Add initialization effect with version checking
  useEffect(() => {
    // Check column version and update if needed
    if (selectedColumns && sectionKey) {
      clearConfigIfOutdated(sectionKey, selectedColumns);
    }

    // Check if storage is initialized for this section using role-based preferences
    const savedFullOrder = getPreference(`fullColumnOrder_${sectionKey}`);
    const savedSelectedColumns = getPreference(`selectedColumns_${sectionKey}`);

    // Initialize both if they don't exist
    if (!savedFullOrder && !savedSelectedColumns && selectedColumns) {
      setPreference(`fullColumnOrder_${sectionKey}`, selectedColumns);
      setPreference(`selectedColumns_${sectionKey}`, selectedColumns);
      // Set the initial version for this section using role-based preferences
      const currentVersion = getPreference(`columnConfigVersion_${sectionKey}`, '1');
      // setPreference(`columnConfigVersion_${sectionKey}`, currentVersion);
      console.log(`BondTable: Initialized storage for section ${sectionKey}`);

      // Apply render functions to the columns
      const columnsWithRenders = applyRenderFunctions(selectedColumns, source, sectionKey);
      setRawColumns(columnsWithRenders);
    }
  }, [sectionKey, selectedColumns, source]);

  // **Resizing Logic**
  const startResizing = (e, columnKey) => {
    e.stopPropagation();
    if (columnKey === "protocol") return;
    resizingColumnRef.current = columnKey;
    initialWidthRef.current = columnWidths[columnKey] || 150;
    initialXRef.current = e.clientX;
    document.addEventListener("mousemove", handleResizing);
    document.addEventListener("mouseup", stopResizing);
    document.body.style.cursor = "col-resize";
  };

  const handleResizing = (e) => {
    //if column is viewinventory return
    if (resizingColumnRef.current === "viewInventory" || resizingColumnRef.current === "protocol") {
      return;
    }
    if (resizingColumnRef.current) {
      const delta = e.clientX - initialXRef.current;
      const newWidth = initialWidthRef.current + delta;

      // Define different minimum widths for specific columns
      let minWidth = 50;

      // Set a larger minimum width for viewInventory to prevent button cutting
      if (resizingColumnRef.current === "viewInventory") {
        minWidth = 140; // Match the default width for viewInventory so it can't go lower
      }

      // Force protocol column to max 100px
      const maxWidth = resizingColumnRef.current === "protocol" ? 100 : 500;
      const clampedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumnRef.current]: clampedWidth,
      }));
    }
  };

  const stopResizing = () => {
    resizingColumnRef.current = null;
    document.removeEventListener("mousemove", handleResizing);
    document.removeEventListener("mouseup", stopResizing);
    document.body.style.cursor = "";
  };

  // **Formatting Functions**
  const formatNumber = (value, columnKey, rowData) => {
    if (value === undefined || value === null || value === "") {
      // Special handling for TTL column
      if (columnKey === "orderExpiryDtTime") {
        return "N/A";
      }
      return "---";
    }

    // ALWAYS enforce 2-decimal rounding for accruedInterest regardless of any user config or global sets
    if (columnKey === "accruedInterest") {
      const num = Number(value);
      if (isNaN(num)) return "---";
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    // Format date in DDMonYY HH:MM format (e.g., 12Mar25 15:43)
    const formatDateToDDMonYY = (dateStr, showTime = true) => {
      try {
        // Ensure timestamp is treated as UTC by appending 'Z' if not present
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith("Z")) {
          valueStr += "Z";
        }

        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";

        const day = date.getUTCDate();
        const month = date.toLocaleString("en-US", { month: "short" });
        const year = date.getUTCFullYear().toString().slice(-2);

        // Add time in 24-hour format only if showTime is true
        if (showTime) {
          const hours = date.getUTCHours().toString().padStart(2, "0");
          const minutes = date.getUTCMinutes().toString().padStart(2, "0");
          return `${day}${month}${year} ${hours}:${minutes}`;
        }

        // Return only the date portion
        return `${day}${month}${year}`;
      } catch (e) {
        console.error("Error formatting date:", e, "for value:", dateStr);
        return "---";
      }
    };

    // New helper to format maturity as MM/DD/YYYY
    const formatDateToMMDDYYYY = (dateStr) => {
      try {
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith("Z")) {
          valueStr += "Z";
        }
        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";
        const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(date.getUTCDate()).padStart(2, "0");
        const yyyy = date.getUTCFullYear();
        return `${mm}/${dd}/${yyyy}`;
      } catch (e) {
        console.error("Error formatting date to MM/DD/YYYY:", e, "for value:", dateStr);
        return "---";
      }
    };

    // Special handling for orderExpiryDtTime - show N/A for CLOB protocol
    if (columnKey === "orderExpiryDtTime") {
      // Check for CLOB protocol in the row data
      if (rowData && (rowData.protocol === "CLOB" || !rowData.protocol)) {
        return "N/A";
      }

      try {
        // Ensure timestamp is treated as UTC by appending 'Z' if not present
        let valueStr = value.toString();
        if (!valueStr.endsWith("Z")) {
          valueStr += "Z";
        }

        // Use let instead of const so we can reassign it
        let date = new Date(valueStr);

        if (!isNaN(date.getTime())) {
          // Format in 24-hour UTC time (HH:MM:SS UTC)
          const timeString = date.toISOString().split("T")[1].substring(0, 8);
          return timeString;
        }
        console.error("Invalid date after parsing:", value);
        return "---";
      } catch (e) {
        console.error("Error formatting expiry time:", e, "for value:", value);
        return "---";
      }
    }

    if (columnKey === "insured" || columnKey === "altMinTax") {
      return value === 1 ? "Yes" : value === 0 ? "No" : "---";
    }

    // Fields that should not be rounded - show as is
    const noRoundingFields = [
      // "dollarConvexity", "dv01", "duration", "macaulayDuration", 
      // "modifiedDuration", "modifiedConvexity", "yield", "yieldValuePP", "yieldToWorst",
      // "accruedInterest", "accruedInterestValue",
      "coupon"
    ];

    if (noRoundingFields.includes(columnKey)) {
      if (typeof value === "number" || !isNaN(Number(value))) {
        const num = Number(value);
        return num.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 20, // Allow many decimal places to show as is
          useGrouping: true
        });
      }
      return "---";
    }

    // Always show decimals for other numeric columns
    if (NUMERIC_COLUMNS.has(columnKey)) {
      if (typeof value === "number" || !isNaN(Number(value))) {
        const num = Number(value);
        // Use 5 decimals for high-precision numeric columns, 0 for integer-like columns, otherwise 2
        const integerColumns = [
          "qty", "quantity", "minQty", "maxQty", "numberOfRungs", "currentQuantity", "endingQuantity"
        ];
        const decimalPlaces = integerColumns.includes(columnKey)
          ? 0
          : HIGH_PRECISION_NUMERIC.has(columnKey)
            ? 5
            : 2;
        return num.toLocaleString("en-US", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      }
      return "---";
    }
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime", "maturityDate", "callEndDate", "callStartDate"];
    if (dateColumns.includes(columnKey) && value) {
      // For maturity column, render as MM/DD/YYYY (no time)
      if (columnKey === "maturity" || columnKey === "maturityDate") {
        return formatDateToMMDDYYYY(value);
      }

      // For other date columns keep previous behavior (DDMonYY, optionally without time)
      if (columnKey === "nextCallDate" || columnKey === "callEndDate" || columnKey === "callStartDate") {
        return formatDateToDDMonYY(value, false);
      }
      return formatDateToDDMonYY(value);
    }

    if (columnKey === "coupon") {
      return value !== null && value !== undefined ? value : "---";
    }

    return value;
  };

  const isNumericOrDate = (value, columnKey) => {
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime", "maturityDate", "callEndDate", "callStartDate"];
    return NUMERIC_COLUMNS.has(columnKey) || dateColumns.includes(columnKey);
  };

  const isBuySellColumn = (columnKey) => {
    return columnKey === "side" || columnKey === "action";
  };

  const isTypeLikeColumn = (columnKey) => {
    return (
      columnKey === "type" ||
      columnKey === "bondType" ||
      columnKey === "bondSubType"
    );
  };

  // Helper function to identify columns that should be bold
  const isBoldColumn = (columnKey) => {
    return ["securityMarketValue", "totalGL", "realizedGL", "unrealizedGL"].includes(columnKey);
  };

  // Helper function to identify columns that should be green
  const isGreenColumn = (columnKey) => {
    return ["totalGL", "realizedGL", "unrealizedGL"].includes(columnKey);
  };

  // **Context Menu Handlers**
  const handleContextMenu = (event, rowData) => {
    event.preventDefault();
    if (isStagingOrder(rowData) && userRole === "Trader") {
      setSelectedRowData(rowData);
      setContextMenu(
        contextMenu === null
          ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
          : null
      );
    }
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleSendToMarket = () => {
    handleCloseContextMenu();
    setOrderEntryOpen(true);
  };

  // Virtual row: use token-based colors (no hardcoded rgba)
  const rowHeight = "40px";
  const cellPadding = "4px 8px";
  const virtualRowBgColor = tokens.colors.defaultGridHoverColor;
  const virtualRowHoverBgColor = tokens.colors.defaultGridHoverColor;

  // Check if all rows on the current page are selected
  const selectedIds = new Set(selectedRows.map((r) => r.id));
  const pageRowIds = (filteredData || []).map((r) => r.id);
  const selectedOnPage = pageRowIds.filter((id) => selectedIds.has(id)).length;
  const isAllSelected = showCheckboxes && pageRowIds.length > 0 && selectedOnPage === pageRowIds.length;
  const isIndeterminate = showCheckboxes && selectedOnPage > 0 && selectedOnPage < pageRowIds.length;

  // Handle select all checkbox
  const handleSelectAllChange = (event) => {
    if (event.target.checked) {
      onSelectAll();
    } else {
      onClearSelection();
    }
  };

  // Handle individual row selection
  const handleRowSelectionChange = (rowId) => {
    onRowSelectionChange(rowId);
  };

  // Cell renderer for editable columns
  const cellRenderer = (row, column, formattedValue, rowIndex) => {
    // Check if this column has a custom renderer
    const config = EDITABLE_FIELDS.find((field) => field.columnKey === column.key);
    if (config) {
      const rendererMap = {
        tradeQtyCellRenderer,
        endingQtyCellRenderer,
        tradeValueCellRenderer,
        endingSecurityMarketValueCellRenderer,
        endingWeightCellRenderer,
      };

      const renderer = rendererMap[config.rendererPropName];
      if (typeof renderer === "function") {
        // The renderer itself handles the includeInRebalancing check
        return renderer(row[column.key], row, null, rowIndex);
      }
    }

    return formattedValue;
  };

  const isZero = (value) => {
    if (value === null || value === undefined || value === "") return false;
    const num = Number(value);
    return Number.isFinite(num) && num === 0;
  };

  const shouldShowDashForSide = (rowData) =>
    (isZero(rowData?.tradeQuantity) || isZero(rowData?.tradeValue));


  return (
    <Box
      sx={{
        px: "16px",
        py: "0px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <TableContainer
        component={Paper}
        ref={tableContainerRef}
        sx={{
          height: "100%",
          maxHeight: "100%",
          borderRadius: "0px",
          overflow: "auto",
          position: "relative",
          backgroundColor: tokens.colors.defaultGridRowColor,
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${tokens.colors.defaultGridBorderColor}`,
          },
          "& .vertical-sticky-header": {
            position: "sticky",
            top: 0,
            backgroundColor: tokens.colors.defaultGridHeaderBackground,
            zIndex: 4,
          },
          "& .sticky-column": {
            position: "sticky !important",
            left: 0,
            zIndex: 5,
            // removed !important so virtual row override can win
            backgroundColor: tokens.colors.defaultGridRowColor,
            borderRight: `1px solid ${tokens.colors.defaultGridBorderColor}`,
          },
          "& .virtual-sticky-column": {
            position: "sticky !important",
            left: 0,
            zIndex: 5,
            backgroundColor: `${virtualRowBgColor} !important`,
            borderRight: `1px solid ${tokens.colors.defaultGridBorderColor}`,
          },
          "& .sticky-header-and-column": {
            position: "sticky !important",
            top: 0,
            left: 0,
            zIndex: 6,
            backgroundColor: tokens.colors.defaultGridHeaderBackground,
          },
          // Force all cells in a virtual row to use the virtual bg
          '& tr[data-virtual="true"] .MuiTableCell-root': {
            backgroundColor: `${virtualRowBgColor} !important`,
          },
          '& tr[data-virtual="true"]:hover .MuiTableCell-root': {
            backgroundColor: `${virtualRowHoverBgColor} !important`,
          },
        }}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <Droppable droppableId="columns" direction="horizontal" type="column">
                {(provided) => (
                  <TableRow ref={provided.innerRef} {...provided.droppableProps}>
                    {/* Checkbox column - not draggable */}
                    {showCheckboxes && (
                      <TableCell
                        sx={{
                          backgroundColor: tokens.colors.defaultGridHeaderBackground,
                          color: tokens.colors.headerFontColour,
                          padding: "4px",
                          fontWeight: 700,
                          height: rowHeight,
                          userSelect: "none",
                          borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`,
                          width: 50,
                          textAlign: "center",
                          cursor: "default",
                        }}
                        className="vertical-sticky-header sticky-column sticky-header-and-column"
                      >
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={handleSelectAllChange}
                          sx={{
                            color: (theme) => theme.palette.text.secondary,
                            "&.Mui-checked": {
                              color: (theme) => theme.palette.success.main,
                            },
                            "&.MuiCheckbox-indeterminate": {
                              color: (theme) => theme.palette.text.primary,
                              "& .MuiSvgIcon-root": {
                                color: (theme) => theme.palette.text.primary + " !important",
                                fill: (theme) => theme.palette.text.primary + " !important",
                              },
                            },
                            "&:hover": {
                              backgroundColor: (theme) => theme.palette.action.hover,
                            },
                            padding: "2px",
                            "& .MuiSvgIcon-root": {
                              fontSize: "14px",
                              color: "inherit",
                              fill: "currentColor",
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column, index) => (
                      <Draggable key={column.key} draggableId={column.key} index={index}>
                        {(provided) => (
                          <TableCell
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            sx={{
                              backgroundColor: tokens.colors.defaultGridHeaderBackground,
                              color: tokens.colors.headerFontColour,
                              padding: cellPadding,
                              fontWeight: 700,
                              height: rowHeight,
                              userSelect: "none",
                              borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`,
                              width: column.key === "protocol" ? 100 : columnWidths[column.key] || 150,
                              textAlign: column.key === "viewInventory" ? "center" : undefined,
                              cursor: "grab",
                              // position: "relative",
                            }}
                            className={
                              source === "inventory" && index === 0
                                ? "vertical-sticky-header"
                                : "vertical-sticky-header"
                            }
                          >
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  column.key === "viewInventory"
                                    ? "center"
                                    : isNumericOrDate(null, column.key)
                                      ? "flex-end"
                                      : isBuySellColumn(column.key)
                                        ? "center"
                                        : "flex-start",
                                height: "100%",
                                width: "100%",
                                paddingRight: "5px",
                                boxSizing: "border-box",
                                cursor: "grab",
                              }}
                            >
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  marginRight: "4px",
                                }}
                                title={column.name}
                              >
                                {column.name}
                              </span>
                              {column.key !== "viewInventory" && (
                                <span
                                  onClick={() => handleSort(column.key)}
                                  style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                                >
                                  {sortConfig.key === column.key ? (
                                    sortConfig.direction === "asc" ? (
                                      <ArrowDropUp fontSize="small" />
                                    ) : (
                                      <ArrowDropDown fontSize="small" />
                                    )
                                  ) : (
                                    <UnfoldMore
                                      fontSize="small"
                                      sx={{ opacity: 0.5, "&:hover": { opacity: 1 } }}
                                    />
                                  )}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: "5px",
                                backgroundColor: tokens.colors.bodyBorderColor50,
                                cursor: column.key === "protocol" ? "default" : "col-resize",
                                pointerEvents: column.key === "protocol" ? "none" : "auto",
                              }}
                              onMouseDown={(e) => startResizing(e, column.key)}
                            />
                          </TableCell>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableRow>
                )}
              </Droppable>
            </TableHead>
            <TableBody>
              {filteredData.map((rowData, rowIndex) => (
                <TableRow
                  key={rowData.orderId || rowData.id || `row-${rowIndex}`}
                  onContextMenu={(e) => handleContextMenu(e, rowData)}
                  data-virtual={rowData?.isVirtualZeroPosition ? "true" : undefined}
                  sx={{
                    cursor: "pointer",
                    height: rowHeight,
                    "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
                    ...(rowData?.isVirtualZeroPosition && {
                      backgroundColor: virtualRowBgColor,
                      "&:hover": { backgroundColor: virtualRowHoverBgColor },
                    }),
                  }}
                >
                  {/* Checkbox cell */}
                  {showCheckboxes && (
                    <TableCell
                      sx={{ height: rowHeight, padding: "4px", width: 50, textAlign: "center" }}
                      className={rowData?.isVirtualZeroPosition ? "virtual-sticky-column" : "sticky-column"}
                    >
                      <Checkbox
                        checked={selectedRows.some((row) => row.id === (rowData.id || rowData.orderId))}
                        onChange={() => handleRowSelectionChange(rowData.id || rowData.orderId)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: (theme) => theme.palette.text.secondary,
                          "&.Mui-checked": { color: (theme) => theme.palette.success.main },
                          "&.MuiCheckbox-indeterminate": {
                            color: (theme) => theme.palette.text.primary,
                            "& .MuiSvgIcon-root": {
                              color: (theme) => theme.palette.text.primary + " !important",
                              fill: (theme) => theme.palette.text.primary + " !important",
                            },
                          },
                          padding: "2px",
                          "& .MuiSvgIcon-root": { fontSize: "14px", color: "inherit", fill: "currentColor" },
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column, colIndex) => {
                    // Handle columns with custom render functions (tradeQuantity, endingQuantity, tradeValue, endingSecurityMarketValue)
                    if (typeof column.render === "function") {
                      return (
                        <TableCell
                          key={column.key}
                          sx={{
                            height: rowHeight,
                            padding: cellPadding,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            backgroundColor: rowData?.isVirtualZeroPosition ? virtualRowBgColor : tokens.colors.defaultGridRowColor,
                            color: tokens.colors.fontColour,
                            width: columnWidths[column.key] || 80,
                            textAlign: column.numeric ? "right" : "left",
                          }}
                          className={
                            source === "inventory" && colIndex === 0
                              ? (rowData?.isVirtualZeroPosition ? "virtual-sticky-column" : "sticky-column")
                              : ""
                          }
                        >
                          {column.render(rowData[column.key], rowData, handleViewJson, rowIndex)}
                        </TableCell>
                      );
                    }

                    // ...existing code (formatting, isBuy/isSell etc.)...
                    const shouldMaskSideValue =
                      (column.key === "side" || column.key === "action") && shouldShowDashForSide(rowData);
                    const displayValue = shouldMaskSideValue ? "---" : rowData[column.key];
                    const isNumOrDate = isNumericOrDate(displayValue, column.key);
                    const isBuy = displayValue === "BUY" || displayValue === "Buy";
                    const isSell = displayValue === "SELL" || displayValue === "Sell";
                    const formattedValue = formatNumber(displayValue, column.key);

                    if (column.key === "protocol") {
                      return (
                        <TableCell
                          key={column.key}
                          sx={{
                            height: rowHeight,
                            padding: cellPadding,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            backgroundColor: rowData?.isVirtualZeroPosition ? virtualRowBgColor : tokens.colors.defaultGridRowColor,
                            color: tokens.colors.fontColour,
                            width: 75,
                          }}
                          className={
                            source === "inventory" && colIndex === 0
                              ? (rowData?.isVirtualZeroPosition ? "virtual-sticky-column" : "sticky-column")
                              : ""
                          }
                        >
                          {rowData[column.key] || "CLOB"}
                        </TableCell>
                      );
                    }

                    if (column.key === "viewInventory") {
                      return (
                        <TableCell
                          key={column.key}
                          sx={{
                            height: rowHeight,
                            padding: cellPadding,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            backgroundColor: rowData?.isVirtualZeroPosition ? virtualRowBgColor : tokens.colors.defaultGridRowColor,
                            color: tokens.colors.fontColour,
                            width: columnWidths[column.key] || 120,
                            position: "relative",
                            isolation: "isolate",
                            textAlign: "center",
                          }}
                          className={
                            source === "inventory" && colIndex === 0
                              ? (rowData?.isVirtualZeroPosition ? "virtual-sticky-column" : "sticky-column")
                              : ""
                          }
                        >
                          {column.render ? (
                            column.render(rowData[column.key], rowData, handleViewJson)
                          ) : (
                            <span style={{ display: "inline-block", width: "100%" }}>---</span>
                          )}
                        </TableCell>
                      );
                    }

                    // Virtual zero-position CUSIP cell
                    const isVirtual = !!rowData.isVirtualZeroPosition;
                    if (column.key === "cusip" && isVirtual) {
                      return (
                        <TableCell
                          key={column.key}
                          sx={{
                            height: rowHeight,
                            padding: cellPadding,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            backgroundColor: virtualRowBgColor,
                            color: tokens.palette.primary,
                            fontWeight: 700,
                            width: columnWidths[column.key] || 150,
                          }}
                          className={
                            source === "inventory" && colIndex === 0
                              ? (rowData?.isVirtualZeroPosition ? "virtual-sticky-column" : "sticky-column")
                              : ""
                          }
                        >
                          <Tooltip title="Zero-position added for rebalancing. Security not part of the current portfolio.">
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCusipClick(rowIndex, rowData);
                                }}
                              >
                                {rowData[column.key] || "---"}
                              </span>
                              {source !== "order-preview" && source !== "portfolio-play" && (
                                <>
                                  <EditOutlinedIcon
                                    fontSize="inherit"
                                    sx={{ fontSize: 14, color: tokens.palette.primary, opacity: 0.9, cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEditVirtualZeroPosition && onEditVirtualZeroPosition(rowData);
                                    }}
                                  />
                                  <DeleteOutlineIcon
                                    fontSize="inherit"
                                    sx={{ fontSize: 16, color: tokens.palette.danger, opacity: 0.9, cursor: "pointer" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteVirtualZeroPosition && onDeleteVirtualZeroPosition(rowData);
                                    }}
                                  />
                                </>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={column.key}
                        sx={{
                          height: rowHeight,
                          padding: cellPadding,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          textAlign: isBuySellColumn(column.key)
                            ? "center"
                            : isTypeLikeColumn(column.key)
                              ? "left"
                              : isNumOrDate
                                ? "right"
                                : "left",
                          backgroundColor: rowData?.isVirtualZeroPosition ? virtualRowBgColor : tokens.colors.defaultGridRowColor,
                          color: tokens.colors.fontColour,
                          fontWeight:
                            isSell || isBuy
                              ? 700
                              : ["securityMarketValue", "totalGL", "realizedGL", "unrealizedGL"].includes(column.key)
                                ? 700
                                : 400,
                          width: column.key === "protocol" ? 100 : columnWidths[column.key] || 150,
                        }}
                        className={
                          source === "inventory" && colIndex === 0
                            ? (rowData?.isVirtualZeroPosition ? "virtual-sticky-column" : "sticky-column")
                            : ""
                        }
                        onClick={(e) => {
                          if (column.key === "cusip") {
                            e.stopPropagation();
                            handleCusipClick(rowIndex, rowData);
                          }
                        }}
                      >
                        {column.render ? (
                          column.render(rowData[column.key], rowData, handleViewJson)
                        ) : (
                          <>
                            {(isBuy || isSell) && source === "inventory" && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: isSell
                                    ? withAlpha(tokens.colors.sellBgColor, 0.1)
                                    : withAlpha(tokens.colors.buyBgColor, 0.1),
                                  zIndex: 1,
                                  isolation: "isolate",
                                  mixBlendMode: "normal",
                                }}
                              />
                            )}
                            <span
                              style={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: isBuySellColumn(column.key)
                                  ? "center"
                                  : isTypeLikeColumn(column.key)
                                    ? "left"
                                    : isNumOrDate
                                      ? "right"
                                      : "left",
                                lineHeight: rowHeight,
                                textDecoration:
                                  ((isBuy || isSell) && source === "inventory") || column.key === "cusip"
                                    ? "underline"
                                    : "none",
                                position: "relative",
                                zIndex: 2,
                                color: getValueColor(column.key, isBuy, isSell),
                                fontWeight:
                                  isSell || isBuy || ["securityMarketValue", "totalGL", "realizedGL", "unrealizedGL"].includes(column.key)
                                    ? 700
                                    : 400,
                                ...(() => {
                                  // In portfolio-play mode, all rows are editable regardless of includeInRebalancing.
                                  // In other modes, only rows included in rebalancing get editable styling.
                                  const isIncludedInRebalancing = source === "portfolio-play" || rowData?.includeInRebalancing === true;
                                  // Only apply editable styling if column is editable AND row qualifies
                                  const shouldApplyEditableStyle = isEditableColumn(column.key) && isIncludedInRebalancing;
                                  return shouldApplyEditableStyle
                                    ? {
                                      color: tokens.palette.primary,
                                      textDecoration: "underline",
                                      cursor: "pointer",
                                      fontWeight: 700,
                                    }
                                    : {};
                                })(),
                              }}
                              title={formattedValue}
                              onClick={() => {
                                if (column.key === "cusip") {
                                  handleCusipClick(rowIndex, rowData);
                                } else if (isBuy || isSell) {
                                  handleBuyClick(rowIndex, rowData);
                                } else if (isEditableColumn(column.key)) {
                                  // handled by custom renderer
                                } else {
                                  handleRowClick(rowIndex, rowData);
                                }
                              }}
                            >
                              {cellRenderer(rowData, column, formattedValue, rowIndex)}
                            </span>
                          </>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DragDropContext>
      </TableContainer>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleSendToMarket}>Send to market</MenuItem>
      </Menu>

      {orderEntryOpen && selectedRowData && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source="sendToMarket"
          initialData={selectedRowData}
          readOnly={false}
          token={localStorage.getItem("token")}
          onOrderAdded={onOrderAdded}
        />
      )}
    </Box>
  );
};

export default BondTable;
